/**
 * Presentation model for the relationship map: colours, neighbourhood index and
 * the projection of the API payload into simulation nodes.
 *
 * Kept apart from the renderer so the colour vocabulary is defined exactly once
 * and the legend, the canvas and the inspector cannot drift apart.
 */

import { nodeRadius, seedPosition, type SimEdge, type SimNode } from './simulation';
import type { GraphEdge, GraphNode, GraphResponse } from '../../lib/types';

/**
 * One colour per class. Picked from a colour-blind-safe palette (Okabe-Ito plus
 * two extras) and deliberately NOT taken from the theme tokens: these have to
 * stay distinguishable from each other, not match the brand accent.
 */
export const CLASS_COLORS: Readonly<Record<string, string>> = {
  Kunde: '#0072b2',
  ProduktType: '#ff5e00',
  ProduktVersion: '#e69f00',
  KomponenteType: '#009e73',
  KomponenteVersion: '#56b4e9',
  ProduktVersionKomponente: '#8aa29e',
  ProduktInstanz: '#7b5ea7',
  CVE: '#d92d20',
  VulnerabilityEvent: '#a6761d',
  Benachrichtigung: '#0e9594',
};

export const FALLBACK_COLOR = '#8a8a8a';

export function classColor(klass: string): string {
  return CLASS_COLORS[klass] ?? FALLBACK_COLOR;
}

/** Ring colour for a node carrying a risk verdict ('' = no verdict, no ring). */
export function stateColor(state: string): string | null {
  if (state === 'danger') return '#d92d20';
  if (state === 'warn') return '#e69f00';
  if (state === 'ok') return '#0a9d55';
  return null;
}

export interface GraphModel {
  nodes: SimNode[];
  edges: SimEdge[];
  byId: Map<string, SimNode>;
  /** node id -> ids of its direct neighbours (both directions). */
  neighbours: Map<string, Set<string>>;
  /** node id -> the edges touching it, for the inspector. */
  incident: Map<string, SimEdge[]>;
}

/**
 * Build the simulation graph. Existing positions are carried over by node id so
 * a filter change or a reload does not throw away a layout the user has already
 * read and dragged into shape.
 */
export function buildModel(
  data: GraphResponse,
  previous?: Map<string, SimNode>,
): GraphModel {
  const classOrder = data.classes.map((entry) => entry.name);
  const byId = new Map<string, SimNode>();

  const nodes = data.nodes.map((raw: GraphNode, index: number): SimNode => {
    const old = previous?.get(raw.id);
    const node: SimNode = {
      id: raw.id,
      klass: raw.klass,
      label: raw.label,
      detail: raw.detail,
      context: raw.context ?? '',
      state: raw.state,
      cardId: raw.cardId,
      degree: raw.degree,
      r: nodeRadius(raw.degree),
      x: old?.x ?? 0,
      y: old?.y ?? 0,
      vx: 0,
      vy: 0,
      fx: old?.fx ?? null,
      fy: old?.fy ?? null,
      hidden: false,
    };
    if (!old) {
      const classIndex = classOrder.indexOf(raw.klass);
      seedPosition(node, classIndex < 0 ? index % 8 : classIndex, classOrder.length);
    }
    byId.set(node.id, node);
    return node;
  });

  const edges: SimEdge[] = [];
  const neighbours = new Map<string, Set<string>>();
  const incident = new Map<string, SimEdge[]>();

  for (const raw of data.edges as readonly GraphEdge[]) {
    const source = byId.get(raw.source);
    const target = byId.get(raw.target);
    if (!source || !target) continue;
    const edge: SimEdge = { id: raw.id, label: raw.label, source, target };
    edges.push(edge);
    for (const [a, b] of [
      [raw.source, raw.target],
      [raw.target, raw.source],
    ] as const) {
      const set = neighbours.get(a) ?? new Set<string>();
      set.add(b);
      neighbours.set(a, set);
      const list = incident.get(a) ?? [];
      list.push(edge);
      incident.set(a, list);
    }
  }

  return { nodes, edges, byId, neighbours, incident };
}

/**
 * Ids within `depth` hops of `rootId` (the root included).
 * `depth <= 0` or an unknown root yields an empty set, which callers read as
 * "no focus" — the whole graph stays lit.
 */
export function neighbourhood(
  model: GraphModel,
  rootId: string | null,
  depth: number,
): Set<string> {
  const out = new Set<string>();
  if (!rootId || depth <= 0 || !model.byId.has(rootId)) return out;
  out.add(rootId);
  let frontier = [rootId];
  for (let hop = 0; hop < depth; hop += 1) {
    const next: string[] = [];
    for (const id of frontier) {
      for (const neighbour of model.neighbours.get(id) ?? []) {
        if (out.has(neighbour)) continue;
        out.add(neighbour);
        next.push(neighbour);
      }
    }
    if (next.length === 0) break;
    frontier = next;
  }
  return out;
}
